import { EXPERIENCE_CONFIG } from './config.js';
import { createReadinessGate } from './readiness-gate.js';
import { assetUrl } from '../core/asset-url.js';
import { createTheme } from '../core/theme.js';
import { createViewport } from '../core/viewport.js';
import { createScene } from '../scene/create-scene.js';
import { createRenderer } from '../scene/create-renderer.js';
import { createLighting } from '../scene/create-lighting.js';
import { createCameraController } from '../scene/camera-controller.js';
import { createRenderLoop } from '../scene/render-loop.js';
import { loadWorld } from '../scene/load-world.js';
import { loadTextures } from '../scene/materials/load-textures.js';
import { createMaterialLibrary } from '../scene/materials/create-material-library.js';
import { createLiquidWaveMaterial } from '../scene/waves/create-liquid-wave-material.js';
import { createWaveUpdater } from '../scene/waves/create-wave-updater.js';
import { createGrassMaterialDebugUI } from '../scene/materials/grass-material-debug-ui.js';
import { createIntroController } from '../features/intro/create-intro-controller.js';
import { createDemoController } from '../features/demo/create-demo-controller.js';
import { createDemoQrController } from '../features/demo/create-demo-qr-controller.js';
import { HOTSPOT_CONFIGS } from '../features/hotspots/config.js';
import { createHotspotController } from '../features/hotspots/create-hotspot-controller.js';
import { createLegendController } from '../features/legend/create-legend-controller.js';
import { createInfocardController } from '../features/infocards/create-infocard-controller.js';
import { createInfocardModalController } from '../features/infocards/create-infocard-modal-controller.js';
import { createExperienceController } from '../features/experience/create-experience-controller.js';
import { createStreetMaterialDebugUI } from '../scene/materials/street-material-debug-ui.js';
import { createKollektorGrid } from '../scene/kollektor-grid-debug-ui.js';
import { createArrowController } from '../features/arrows/create-arrow-controller.js';

export function createApp(targetDocument = document) {
   const canvas = targetDocument.querySelector('canvas.webgl');
   if (!canvas) throw new Error('Missing canvas.webgl element.');

   const theme = createTheme(targetDocument.documentElement);
   const viewport = createViewport(targetDocument.defaultView);
   const scene = createScene(theme.color('--color-surface-muted'));
   const renderer = createRenderer(canvas);
   const { keyLight } = createLighting(scene);
   const textures = loadTextures(assetUrl);
   const wave = createLiquidWaveMaterial(textures);
   const { materials, groundGridTexture } = createMaterialLibrary({
      textures,
      liquidWaveMaterial: wave.material,
      gridBackground: theme.color('--color-surface-muted'),
   });
   const waveUpdater = createWaveUpdater(wave.uniforms);
   const camera = createCameraController();
   const readiness = createReadinessGate(['startup', 'hotspots']);
   let experience = null;
   let kollektorGrid = null;
   let arrowController = null;
   let destroyed = false;

   const infocards = createInfocardController({
      onPrevious: () => experience?.navigate(-1),
      onNext: () => experience?.navigate(1),
      onBack: () => experience?.reset(),
      targetDocument,
      targetWindow: targetDocument.defaultView,
   });
   const infocardModal = createInfocardModalController({ targetDocument });
   const legend = createLegendController({
      configs: HOTSPOT_CONFIGS,
      onSelect: (key) => experience?.activate(key),
      targetDocument,
   });
   const hotspots = createHotspotController({
      configs: HOTSPOT_CONFIGS,
      onSelect: (key) => experience?.activate(key),
      isExperienceReady: readiness.isReady,
      onPosition: (hotspot, x, y, size) =>
         infocards.positionActive(hotspot, x, y, size),
      targetDocument,
   });

   experience = createExperienceController({
      configs: HOTSPOT_CONFIGS,
      camera,
      hotspots,
      legend,
      infocards,
   });

   const intro = createIntroController({
      enabled: EXPERIENCE_CONFIG.introEnabled,
      animateCameraToInitial: (onComplete) =>
         camera.animateToInitial(onComplete),
      onComplete: () => readiness.markReady('startup'),
      targetDocument,
   });

   const demoQr = createDemoQrController({
      imageUrl: assetUrl('misc/qr-steinexpo.svg'),
      targetDocument,
   });

   const demo = createDemoController({
      configs: HOTSPOT_CONFIGS,
      inactivityMs: EXPERIENCE_CONFIG.demoInactivityMs,
      hotspotDurationMs: EXPERIENCE_CONFIG.demoHotspotDurationMs,
      onIdle: () => {
         infocardModal.close();
         intro.dismiss();
      },
      onStart: () => demoQr.show(),
      onStop: () => demoQr.hide(),
      onActivate: (key) => experience?.activate(key),
      targetDocument,
      targetWindow: targetDocument.defaultView,
   });

   const stopWaitingForReadiness = readiness.onReady(() => {
      legend.reveal();
      demo.notifyReady();
   });
   const stopViewportUpdates = viewport.subscribe((size) => {
      renderer.resize(size);
      camera.resize(size);
   });

   renderer.setTextureAnisotropy(groundGridTexture);

   const renderLoop = createRenderLoop((delta) => {
      waveUpdater.update(delta);
      arrowController?.update(delta);
      hotspots.updateFrame(camera.getCamera(), viewport.getSize());
      renderer.render(scene, camera.getCamera());
   }, targetDocument);

   async function initializeWorld() {
      try {
         const world = await loadWorld({
            url: assetUrl(EXPERIENCE_CONFIG.modelPath, {
               cacheBust: EXPERIENCE_CONFIG.cacheBustModel,
            }),
            scene,
            keyLight,
            materials,
            waveUniforms: wave.uniforms,
            viewport: viewport.getSize(),
         });

         if (destroyed) return;

         kollektorGrid = createKollektorGrid(world.root);

         camera.attach(world.camera, world.cameraTarget, {
            applyIntroOffset: !intro.isDismissed(),
         });
         waveUpdater.attach(world);
         hotspots.initialize(world.root);
         readiness.markReady('hotspots');

         // Initialize Street material debug UI
         if (EXPERIENCE_CONFIG.lilGUIEnabled) {
            const streetMesh = world.root.getObjectByName('Street');
            if (streetMesh && streetMesh.material) {
               createStreetMaterialDebugUI(streetMesh.material);
            } else {
               console.warn(
                  '[Street Material] Street mesh or material not found',
               );
            }

            createGrassMaterialDebugUI(materials.Gras);
         }

         // Initialize arrow controller
         arrowController = createArrowController(scene, world, {
            debugUIEnabled: EXPERIENCE_CONFIG.lilGUIEnabled,
            experience,
         });
      } catch (error) {
         console.error('Error loading GLTF model:', error);
      }
   }

   demo.start();
   intro.start();
   renderLoop.start();
   void initializeWorld();

   return {
      destroy() {
         destroyed = true;
         stopWaitingForReadiness();
         stopViewportUpdates();
         demo.destroy();
         demoQr.destroy();
         renderLoop.stop();
         intro.destroy();
         experience.destroy();
         kollektorGrid?.destroy();
         arrowController?.destroy();
         hotspots.destroy();
         legend.destroy();
         infocardModal.destroy();
         infocards.destroy();
         viewport.destroy();
         renderer.destroy();
      },
   };
}
